import { PlanBenefits } from '@/components/PlanBenefits';
import { Button } from '@/globalComponents/button';
import ImageWithFallback from '@/globalComponents/image';
import { BenefitsType, TierType } from '@/types/tier';
import DownloadIcon from '../../../../../public/images/download.svg';
import styles from './style.module.scss';
import { formatDate } from './utils';
import { useContext, useRef, useState } from 'react';
import { ServiceRequestContext } from '@/context/serviceRequestContext';
import { WebEngageContext } from '@/context/webengageContext';
import { AppScreens, WebEngageEventNames } from '@/types/wegengage';
import { AlertModal } from '@/globalComponents/alertModal';

interface AmcDetailsProps {
  status?: string;
  planType: string;
  startDate: string;
  endDate: string;
  benefits: BenefitsType[];
  invoiceLink: string | null;
  orderPricePerUnit?: number;
}

export const AmcDetails = (props: AmcDetailsProps) => {
  const {
    planType,
    startDate: validFrom,
    endDate: validUpto,
    benefits,
    status,
    invoiceLink,
    orderPricePerUnit,
  } = props;

  const showValidityInfo: boolean = !!(validFrom && validUpto);

  const tier = (planType as string).toLowerCase() as TierType;

  const headerText = status === 'warranty' ? 'Warranty Details' : 'AMC Details';

  const showInVoiceDownloadButton = !(status === 'warranty') && invoiceLink;

  const hidePlanBenefits: boolean =
    (!orderPricePerUnit || orderPricePerUnit === 0) && (!benefits || benefits.length < 1);

  const showAMCDetailsTitle: boolean = showValidityInfo || !hidePlanBenefits;
  const { state: serviceState } = useContext(ServiceRequestContext);
  const { state: webengageState } = useContext(WebEngageContext);
  const { webengageData } = serviceState;
  const [isError, setIsError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadRef = useRef<HTMLAnchorElement>(null);

  const ValidityInfo = () => (
    <div className={styles.infoContainer}>
      <p data-testid="valid-from-label">
        Valid From
        <span className={`block ${styles.infoDescription}`} data-testid="valid-from-value">
          {formatDate(validFrom)}
        </span>
      </p>
      <p data-testid="valid-upto-label">
        Valid Upto
        <span className={`block ${styles.infoDescription}`} data-testid="valid-upto-value">
          {formatDate(validUpto)}
        </span>
      </p>
    </div>
  );

  const downloadInvoice = (downloadLink: RequestInfo) => {
    setIsDownloading(true);
    fetch(downloadLink, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    })
      .then((response) => response.blob())
      .then((blob) =>
        /* istanbul ignore next */
        {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'Invoice.pdf';
          link.click();
          URL.revokeObjectURL(url);
          link.remove();
          setIsDownloading(false);
        },
      )
      .catch(() => {
        setIsError(true);
        setIsDownloading(false);
      });
  };
  const AMCDetailsTitle = () => (
    <div className="flex flex-col md:gap-s6 gap-5">
      <div className="flex justify-between items-center">
        <p
          data-testid={`${headerText}-label`}
          className={`font-bold md:text-xl md:leading-[2.6rem] text-base leading-[1rem] ${styles.label}`}
        >
          {headerText}
        </p>
        {showInVoiceDownloadButton && (
          <Button
            onClick={() => {
              if (downloadRef.current) {
                try {
                  const _we = window.webengage;
                  if (_we) {
                    _we.track(WebEngageEventNames.AMC_DOC_DOWNLOAD_CLICKED, {
                      ...webengageState.webengageDefault,
                      navigated_from: AppScreens.DEVICE_DETAILS_SCREEN,
                      customer_type: webengageData.customer_type,
                      product_selected: webengageData.product_selected,
                      amc_tier: webengageData.amc_tier,
                      amc_plan_duration: webengageData.amc_plan_duration,
                      amc_plan_value: webengageData.amc_plan_value,
                      amc_plan_valid_upto: webengageData.amc_plan_valid_upto,
                      amc_plan_valid_from: webengageData.amc_plan_valid_from,
                      mandatory_service_available: webengageData.mandatory_service_available,
                      mandatory_service_availed: webengageData.mandatory_service_availed,
                      product_flag: webengageData.product_flag,
                      customer_code: webengageData.customer_code,
                    });
                  }
                  downloadRef.current.click();
                } catch (error) {
                  // istanbul ignore next
                  setIsError(true);
                }
              }
            }}
            variant="link"
            classNames={styles.amcDocumentsButton}
            dataTestId="invoice-download-button"
          >
            {isDownloading ? (
              <p>Downloading...</p>
            ) : (
              <>
                <a
                  ref={downloadRef}
                  onClick={() => downloadInvoice(invoiceLink)}
                  className="none"
                />
                <div className="flex items-center md:gap-[0.625rem] gap-[0.375rem]">
                  <p className={styles.amcDocumentslabel} data-testid="download-button-label">
                    AMC Invoice
                  </p>
                  <ImageWithFallback
                    alt={'download'}
                    src={DownloadIcon}
                    className="md:w-6 md:h-6 w-[21px] h-[21px]"
                    data-testid="download-icon"
                  />
                </div>
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col md:gap-7 gap-5">
      {showAMCDetailsTitle && <AMCDetailsTitle />}
      {showValidityInfo && <ValidityInfo />}
      {!hidePlanBenefits && (
        <PlanBenefits
          dataSet={benefits}
          perYearPlan={{
            validity: '12',
            pricing: {
              // @ts-ignore
              price: orderPricePerUnit,
            },
          }}
          tier={tier}
          shouldShowToggleButton={false}
          pricePerYear={false}
          status={status ?? ''}
        />
      )}
      <AlertModal
        open={isError}
        title={'Something went wrong'}
        buttonText={'Retry'}
        onButtonClick={() => {
          setIsError(false);
        }}
        severity="error"
        onClose={() => {
          setIsError(false);
        }}
        showCloseButton={true}
      />
    </div>
  );
};
